import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  Tooltip,
  Chip,
  useTheme,
  IconButton,
  Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate } from 'react-router-dom';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { useNotification } from '../../contexts/NotificationContext';
import { Pages } from '../../utils';

const BANNER_HEIGHT = 56;
const COLLAPSED_HEIGHT = 8;

export const ImpersonationBanner: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isImpersonating, impersonatedRole, impersonatedTier, actualUserName, stopImpersonation } = useImpersonation();
  const { showNotification } = useNotification();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-collapse after 3 seconds, but expand on hover
  useEffect(() => {
    if (isImpersonating && isExpanded) {
      const timer = setTimeout(() => {
        setIsExpanded(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isImpersonating, isExpanded]);

  // Don't add body padding - let the layout handle the space

  const handleStopImpersonation = () => {
    stopImpersonation();
    showNotification({
      type: 'success',
      message: 'Returned to Admin view',
      animation: 'bounce',
    });
    // Redirect to admin dashboard
    navigate(Pages.ADMIN_DASHBOARD);
  };

  if (!isImpersonating || !impersonatedRole) {
    return null;
  }

  const getStatusColor = (role: string): { bg: string; text: string; border: string } => {
    switch (role) {
      case 'Designer':
        return { bg: '#E3F2FD', text: '#1565C0', border: '#90CAF9' }; // Light blue
      case 'Homeowner':
        return { bg: '#F3E5F5', text: '#7B1FA2', border: '#CE93D8' }; // Light purple
      default:
        return { bg: '#FFF3E0', text: '#E65100', border: '#FFB74D' }; // Light orange
    }
  };

  const statusColors = getStatusColor(impersonatedRole);
  const displayText = impersonatedTier 
    ? `${impersonatedRole} - ${impersonatedTier}`
    : impersonatedRole;

  const toggleBanner = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: '#F2F2F2',
        color: '#000',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderBottom: '1px solid #E0E0E0',
        transition: 'all 0.3s ease-in-out',
      }}
      onMouseEnter={() => {
        setIsHovered(true);
        setIsExpanded(true);
      }}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Collapsed Tab */}
      {!isExpanded && (
        <Box
          onClick={toggleBanner}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 0.5,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#E8E8E8',
            },
            transition: 'background-color 0.2s ease-in-out',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <VisibilityIcon sx={{ fontSize: 16, color: '#666' }} />
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
              Viewing as {displayText}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#666' }} />
          </Stack>
        </Box>
      )}

      {/* Expanded Banner */}
      <Collapse in={isExpanded}>
        <Container maxWidth="xl">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ py: 1.5, px: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                }}
              >
                <VisibilityIcon sx={{ color: '#666', fontSize: 20 }} />
              </Box>

              <Stack spacing={0.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: '#333' }}
                  >
                    Viewing as
                  </Typography>
                  <Chip
                    label={displayText}
                    size="small"
                    sx={{
                      backgroundColor: statusColors.bg,
                      color: statusColors.text,
                      border: `1px solid ${statusColors.border}`,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 22,
                      px: 0.5,
                      '& .MuiChip-label': {
                        px: 1,
                      },
                    }}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Tooltip title="Your actual administrator account">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <AdminPanelSettingsIcon sx={{ fontSize: 12, color: '#999' }} />
                      <Typography
                        variant="caption"
                        sx={{ color: '#666', fontSize: '0.7rem' }}
                      >
                        Admin: <strong>{actualUserName}</strong>
                      </Typography>
                    </Stack>
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloseIcon />}
                onClick={handleStopImpersonation}
                sx={{
                  borderColor: '#d22268',
                  color: '#d22268',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  '&:hover': {
                    borderColor: '#d22268',
                    backgroundColor: 'rgba(210, 34, 104, 0.04)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Exit
              </Button>
              
              <IconButton
                size="small"
                onClick={toggleBanner}
                sx={{
                  color: '#666',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.05)',
                  },
                }}
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Container>
      </Collapse>
    </Box>
  );
};
